import React, { useEffect, useState } from "react";
import { Row, Col, Card, Statistic, Typography, Table, Tag, Space, Button, Spin, Avatar, Grid } from "antd";
import {
  FileTextOutlined, UserOutlined, StarOutlined, EyeOutlined,
  PlusOutlined, ArrowRightOutlined, RiseOutlined,
} from "@ant-design/icons";
import { getAllPosts, getAllAuthors } from "../utilscopy/firebase";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const categoryColors = {
  technology: "blue", design: "purple", development: "green",
  productivity: "orange", career: "red",
};

export default function Dashboard({ onNavigate }) {
  const [posts, setPosts] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);

  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  useEffect(() => {
    async function load() {
      try {
        const [p, a] = await Promise.all([getAllPosts(), getAllAuthors()]);
        setPosts(p);
        setAuthors(a);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const featured = posts.filter((p) => p.featured).length;
  const categories = [...new Set(posts.map((p) => p.category))].length;

  const goToPosts = () => {
    if (onNavigate) onNavigate("PostsList");
  };

  const recentColumns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text) => (
        <Text
          strong
          style={{ color: "#1677ff", cursor: "pointer" }}
          onClick={goToPosts}
        >
          {text}
        </Text>
      ),
      ellipsis: true,
      width: 200,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (cat) => (
        <Tag color={categoryColors[cat] || "default"} style={{ borderRadius: 20 }}>
          {cat}
        </Tag>
      ),
      width: 120,
    },
    {
      title: "Author",
      dataIndex: "authorId",
      key: "author",
      render: (authorId) => {
        const author = authors.find((a) => a.id === authorId);
        return author ? (
          <Space>
            <Avatar src={author.avatar} size={24} />
            <Text style={{ fontSize: 13 }}>{author.name}</Text>
          </Space>
        ) : "—";
      },
      width: 160,
    },
    {
      title: "Featured",
      dataIndex: "featured",
      key: "featured",
      render: (v) =>
        v ? <Tag color="gold">★ Featured</Tag> : <Text type="secondary">—</Text>,
      width: 110,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (d) => <Text type="secondary" style={{ fontSize: 13 }}>{d}</Text>,
      width: 120,
    },
    {
      title: "",
      key: "action",
      render: () => (
        <Button
          size="small"
          icon={<ArrowRightOutlined />}
          style={{ borderColor: "#e2e8f0" }}
          onClick={goToPosts}
        />
      ),
      width: 50,
    },
  ];

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Stats */}
      <Row gutter={isMobile ? [12, 12] : [20, 20]} style={{ marginBottom: 28 }}>
        {[
          {
            title: "Total Posts",
            value: posts.length,
            icon: <FileTextOutlined />,
            color: "#1677ff",
            bg: "#eff6ff",
          },
          {
            title: "Featured Posts",
            value: featured,
            icon: <StarOutlined />,
            color: "#f59e0b",
            bg: "#fffbeb",
          },
          {
            title: "Authors",
            value: authors.length,
            icon: <UserOutlined />,
            color: "#10b981",
            bg: "#ecfdf5",
          },
          {
            title: "Categories",
            value: categories,
            icon: <RiseOutlined />,
            color: "#8b5cf6",
            bg: "#f5f3ff",
          },
        ].map((stat, i) => (
          <Col xs={12} sm={12} md={6} key={i}>
            <Card
              style={{ borderRadius: 16, border: "1px solid #f0f0f0", height: "100%" }}
              bodyStyle={{ padding: isMobile ? 14 : 24 }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 14 }}>
                <div
                  style={{
                    width: isMobile ? 38 : 48, height: isMobile ? 38 : 48, borderRadius: 12,
                    background: stat.bg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: isMobile ? 17 : 22, color: stat.color,
                    flexShrink: 0,
                  }}
                >
                  {stat.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <Text type="secondary" style={{ fontSize: isMobile ? 11 : 13 }}>{stat.title}</Text>
                  <div style={{ fontSize: isMobile ? 20 : 28, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                    {stat.value}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Recent Posts Table */}
      <Card
        title={
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              justifyContent: "space-between",
              gap: isMobile ? 12 : 0,
              width: "100%",
              padding: isMobile ? "12px 0" : 0,
            }}
          >
            <Title level={5} style={{ margin: 0 }}>Recent Posts</Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              block={isMobile}
              style={{ background: "#111", borderColor: "#111" }}
              onClick={goToPosts}
            >
              New Post
            </Button>
          </div>
        }
        style={{ borderRadius: 16, border: "1px solid #f0f0f0" }}
        bodyStyle={{ padding: 0 }}
      >
        <Table
          columns={recentColumns}
          dataSource={posts.slice(0, 8)}
          rowKey="id"
          pagination={false}
          size="middle"
          scroll={{ x: 760 }}
          style={{ borderRadius: "0 0 16px 16px", overflow: "hidden" }}
        />
      </Card>
    </div>
  );
}